import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  useCallback
} from "react";
import PropTypes from "prop-types";
import {
  createMuiTheme,
  ThemeProvider as MUIThemeProvider
} from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";

const STORAGE_KEY = "darkMode";

function readStoredDarkMode() {
  try {
    const item = localStorage.getItem(STORAGE_KEY);
    if (item !== null) return JSON.parse(item);
  } catch (_) {
    /* ignore */
  }
  return false;
}

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkModeState] = useState(readStoredDarkMode);

  const setDarkMode = useCallback(updater => {
    setDarkModeState(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (_) {
        /* quota / private mode */
      }
      return next;
    });
  }, []);

  const toggleTheme = useCallback(() => {
    setDarkMode(prev => !prev);
  }, [setDarkMode]);

  const theme = useMemo(
    () =>
      createMuiTheme({
        palette: {
          type: darkMode ? "dark" : "light"
        }
      }),
    [darkMode]
  );

  const contextValue = useMemo(
    () => ({ darkMode, setDarkMode, toggleTheme }),
    [darkMode, setDarkMode, toggleTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useThemeContext = () => useContext(ThemeContext);
